/**
 * Planning-layer configuration — every effort/model/value/budget knob in ONE
 * place, with sensible defaults and a user override file
 * (`~/.futurex/planning.json`, partial deep merge). Nothing in the planner,
 * value detector, budget composer, or selection ranker hardcodes these numbers.
 */
import { existsSync, readFileSync } from 'node:fs';
import type { EffortLevel } from '@fim/futurex-protocol';
import { planningConfigPath } from './config.js';

/** How deep planning goes at a given effort level (the effort→depth mapping). */
export interface EffortProfile {
  /** Max research steps the plan may contain. */
  readonly researchSteps: number;
  /** Tooling candidates considered per decision area. */
  readonly candidatesPerChoice: number;
  /** Verification (typecheck/lint/test) passes scheduled during the build. */
  readonly validationPasses: number;
  /** Upper bound on build tasks in the plan. */
  readonly maxPlanTasks: number;
}

/** How much complexity one step may assume for a given model. */
export interface ModelProfile {
  /** How big a single plan step may be for this model. */
  readonly stepComplexity: 'small' | 'medium' | 'large';
  /** How many related subtasks may be batched into one step. */
  readonly batchFactor: number;
}

/** Inferred project importance. */
export type ValueTier = 'low' | 'standard' | 'high' | 'critical';

export interface ValueTierSpec {
  /** Budget multiplier this tier applies to the effort baseline. */
  readonly multiplier: number;
  readonly description: string;
}

export interface PlanningConfig {
  readonly efforts: Record<EffortLevel, EffortProfile>;
  /** Keyed by model id; `default` is the fallback for unknown models. */
  readonly models: Record<string, ModelProfile>;
  readonly valueTiers: Record<ValueTier, ValueTierSpec>;
  readonly budget: {
    /** Effort sets the baseline; value scales it (see budget.ts — the ONE formula). */
    readonly baseTokensByEffort: Record<EffortLevel, number>;
    /** Absolute output-token ceiling per build, regardless of value tier. */
    readonly hardCapTokens: number;
  };
  readonly selection: {
    /** Ranking weights (normalized at use): popularity + maintenance + docs. */
    readonly weights: { popularity: number; maintenance: number; docs: number };
    /** A release within this window counts as fully maintained. */
    readonly freshnessWindowDays: number;
    /** npm registry fetch timeout; on miss the offline baseline is used. */
    readonly fetchTimeoutMs: number;
  };
  readonly value: {
    /** Heuristic score thresholds (0..1) → tier. */
    readonly lowBelow: number;
    readonly highAtLeast: number;
    readonly criticalAtLeast: number;
    /** Index-chunk counts that qualify a repo as medium / large. */
    readonly repoMediumChunks: number;
    readonly repoLargeChunks: number;
    /** Signed score contributions per detected signal (see value.ts). */
    readonly weights: {
      readonly repoMedium: number;
      readonly repoLarge: number;
      readonly stackBreadth: number;
      readonly criticalKeyword: number;
      readonly lowKeyword: number;
      readonly broadScope: number;
    };
  };
}

export const DEFAULT_PLANNING_CONFIG: PlanningConfig = {
  efforts: {
    off: { researchSteps: 1, candidatesPerChoice: 1, validationPasses: 0, maxPlanTasks: 4 },
    high: { researchSteps: 3, candidatesPerChoice: 2, validationPasses: 1, maxPlanTasks: 8 },
    max: { researchSteps: 6, candidatesPerChoice: 3, validationPasses: 2, maxPlanTasks: 12 },
  },
  models: {
    'deepseek-v4-pro': { stepComplexity: 'large', batchFactor: 3 },
    'deepseek-v4-flash': { stepComplexity: 'medium', batchFactor: 2 },
    default: { stepComplexity: 'small', batchFactor: 1 },
  },
  valueTiers: {
    low: { multiplier: 0.5, description: 'throwaway/prototype — stay conservative' },
    standard: { multiplier: 1, description: 'typical project' },
    high: { multiplier: 1.5, description: 'substantial codebase or stated importance' },
    critical: { multiplier: 2, description: 'production/critical signals — maximum care' },
  },
  budget: {
    baseTokensByEffort: { off: 30_000, high: 80_000, max: 200_000 },
    hardCapTokens: 500_000,
  },
  selection: {
    weights: { popularity: 0.5, maintenance: 0.3, docs: 0.2 },
    freshnessWindowDays: 180,
    fetchTimeoutMs: 1_500,
  },
  value: {
    lowBelow: 0.25,
    highAtLeast: 0.55,
    criticalAtLeast: 0.8,
    repoMediumChunks: 300,
    repoLargeChunks: 2_000,
    weights: {
      repoMedium: 0.1,
      repoLarge: 0.2,
      stackBreadth: 0.1,
      criticalKeyword: 0.3,
      lowKeyword: -0.3,
      broadScope: 0.15,
    },
  },
};

type Plain = Record<string, unknown>;

function isPlain(v: unknown): v is Plain {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** Defaults ⊕ user overrides, object-by-object; scalars/arrays replace wholesale. */
function deepMerge<T>(base: T, override: unknown): T {
  if (!isPlain(base) || !isPlain(override)) return base;
  const out: Plain = { ...base };
  for (const [key, value] of Object.entries(override)) {
    const prior = (base as Plain)[key];
    if (isPlain(prior) && isPlain(value)) out[key] = deepMerge(prior, value);
    else if (typeof value === typeof prior && value !== null) out[key] = value;
    // type-mismatched overrides are ignored (defensive: the file is user-edited)
  }
  return out as T;
}

/**
 * The planning config: defaults merged with `~/.futurex/planning.json` when
 * present. An unreadable file degrades to the defaults rather than failing.
 */
export function loadPlanningConfig(): PlanningConfig {
  const path = planningConfigPath();
  if (!existsSync(path)) return DEFAULT_PLANNING_CONFIG;
  try {
    const raw: unknown = JSON.parse(readFileSync(path, 'utf8'));
    return deepMerge(DEFAULT_PLANNING_CONFIG, raw);
  } catch {
    return DEFAULT_PLANNING_CONFIG;
  }
}

const FALLBACK_MODEL_PROFILE: ModelProfile = { stepComplexity: 'small', batchFactor: 1 };

/** The model profile for a model id, falling back to `default`. */
export function modelProfile(cfg: PlanningConfig, model: string): ModelProfile {
  return cfg.models[model] ?? cfg.models.default ?? FALLBACK_MODEL_PROFILE;
}
